import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { 
  getPendingUsers, 
  updateRoleAndActivate, 
  getUsersByRole, 
  getSubordinatesByParentId, 
} from '../api/api';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/Layout/AdminLayout';
import { useAuth } from '../context/AuthContext'; 
import { User, UpdateRoleRequest } from '../api/types'; 

// --- User State Interface for Pending Approvals ---
interface PendingUserState {
  selectedRole: string;
  selectedParentId: number | null; 
  
  // Selected IDs in the multi-level selection hierarchy
  selectedAdminId: number | null;     
  selectedManagerId: number | null;   
  selectedTeamLeaderId: number | null;
  
  admins: User[];
  managers: User[];
  teamLeaders: User[];
  loading: boolean;
}

const initialUserState: PendingUserState = {
  selectedRole: '',
  selectedParentId: null,
  selectedAdminId: null, 
  selectedManagerId: null, 
  selectedTeamLeaderId: null, 
  admins: [],
  managers: [],
  teamLeaders: [],
  loading: false,
};

// --- Helper Function to Clean Role Prefix ---
const cleanRole = (role: string | undefined): string => {
  if (!role) return '';
  const upperRole = role.toUpperCase();
  if (upperRole.startsWith('ROLE_')) {
    return upperRole.substring(5); 
  }
  return upperRole;
};


// --- Main Component ---
const PendingApprovalsPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [userStates, setUserStates] = useState<Record<number, PendingUserState>>({}); 
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  
  const safeCurrentUserRole = cleanRole(currentUser?.role); 
  const currentUserId = currentUser?.id || null; 
  const SUPERADMIN_ID = 1; 

  
  useEffect(() => {
    const fetchPendingUsers = async () => {
      try {
        const response = await getPendingUsers();
        setPendingUsers(response.data);
        
        const initialStates: Record<number, PendingUserState> = {};
        response.data.forEach(user => {
            initialStates[user.id] = initialUserState; 
        });
        setUserStates(initialStates);
      } catch (e: any) {
        console.error("Error fetching pending users:", e); 
        setError(e.response?.data?.message || e.message || 'Failed to load pending users.');
      }
    };
    fetchPendingUsers();
  }, []); 

const getAssignableRoles = useCallback((userRole: string) => {
    switch (userRole) {
      case 'SUPERADMIN': 
        return ['ADMIN', 'MANAGER', 'TEAM_LEADER', 'AGENT'];
      case 'ADMIN': 
        return ['MANAGER', 'TEAM_LEADER', 'AGENT'];
      case 'MANAGER': 
        return ['TEAM_LEADER', 'AGENT'];
      default:
        return [];
    }
}, []);
  
  const fetchAdmins = useCallback(async (userId: number) => {
    setUserStates(prev => ({
      ...prev,
      [userId]: { ...prev[userId], loading: true, admins: [] },
    }));
    try {
      const response = await getUsersByRole('ADMIN'); 
      setUserStates(prev => ({
        ...prev,
        [userId]: { ...prev[userId], admins: response.data, loading: false },
      }));
    } catch (e) {
      console.error("Error fetching admins:", e);
      setUserStates(prev => ({
        ...prev,
        [userId]: { ...prev[userId], admins: [], loading: false },
      }));
    }
  }, []);

  const fetchManagers = useCallback(async (userId: number, parentId: number) => {
    setUserStates(prev => ({
      ...prev,
      [userId]: { ...prev[userId], loading: true, managers: [] },
    }));
    try {
      const response = await getSubordinatesByParentId(parentId); 
      setUserStates(prev => ({
        ...prev,
        [userId]: { 
            ...prev[userId], 
            managers: response.data.filter(u => u.role === 'MANAGER'), 
            loading: false 
        },
      }));
    } catch (e) {
      console.error("Error fetching managers:", e);
      setUserStates(prev => ({
        ...prev,
        [userId]: { ...prev[userId], managers: [], loading: false },
      }));
    }
  }, []);

  const fetchTeamLeaders = useCallback(async (userId: number, parentId: number) => {
    setUserStates(prev => ({
      ...prev,
      [userId]: { ...prev[userId], loading: true, teamLeaders: [] },
    }));
    try {
      const response = await getSubordinatesByParentId(parentId);
      setUserStates(prev => ({
        ...prev,
        [userId]: { 
            ...prev[userId], 
            teamLeaders: response.data.filter(u => u.role === 'TEAM_LEADER'), 
            loading: false 
        },
      }));
    } catch (e) {
      console.error("Error fetching team leaders:", e);
      setUserStates(prev => ({
        ...prev,
        [userId]: { ...prev[userId], teamLeaders: [], loading: false },
      }));
    }
  }, []);
  
  // --- Change Handlers ---
  const handleRoleChange = useCallback((userId: number, newRole: string) => {
    const newState: Partial<PendingUserState> = { 
      selectedRole: newRole, 
      selectedParentId: null, 
      selectedAdminId: null, 
      selectedManagerId: null, 
      selectedTeamLeaderId: null,
      admins: [], managers: [], teamLeaders: []
    };

    let finalParentId: number | null = null;
    let fetchNextLevel = false;

    if (safeCurrentUserRole === 'SUPERADMIN') {
      if (newRole === 'ADMIN') {
        finalParentId = SUPERADMIN_ID; 
      } else if (newRole === 'MANAGER' || newRole === 'TEAM_LEADER' || newRole === 'AGENT') {
        fetchNextLevel = true; 
      }
    } else if (safeCurrentUserRole === 'ADMIN') {
      if (newRole === 'MANAGER') {
        finalParentId = currentUserId; 
      } else if (newRole === 'TEAM_LEADER' || newRole === 'AGENT') {
        fetchNextLevel = true; 
      }
    } else if (safeCurrentUserRole === 'MANAGER') {
      if (newRole === 'TEAM_LEADER') {
        finalParentId = currentUserId; 
      } else if (newRole === 'AGENT') {
        fetchNextLevel = true; 
      }
    }

    newState.selectedParentId = finalParentId;

    setUserStates(prev => {
        const updatedState = { ...prev[userId], ...newState as PendingUserState };
        if (fetchNextLevel) {
            if (safeCurrentUserRole === 'SUPERADMIN' && (newRole === 'MANAGER' || newRole === 'TEAM_LEADER' || newRole === 'AGENT')) {
                fetchAdmins(userId);
            } else if (safeCurrentUserRole === 'ADMIN' && (newRole === 'TEAM_LEADER' || newRole === 'AGENT') && currentUserId) {
                fetchManagers(userId, currentUserId);
            } else if (safeCurrentUserRole === 'MANAGER' && newRole === 'AGENT' && currentUserId) {
                fetchTeamLeaders(userId, currentUserId);
            }
        }
        return {
            ...prev,
            [userId]: updatedState,
        };
    });

  }, [safeCurrentUserRole, currentUserId, fetchAdmins, fetchManagers, fetchTeamLeaders]);


  const handleParentSelectChange = useCallback((userId: number, parentType: 'admin' | 'manager' | 'tl', newParentIdStr: string) => {
    const id = newParentIdStr === '' ? null : parseInt(newParentIdStr, 10); 
    
    const userState = userStates[userId];
    if (!userState) return;
    const newRole = userState.selectedRole;
    
    let update: Partial<PendingUserState> = {
        selectedParentId: null, 
        selectedAdminId: userState.selectedAdminId,
        selectedManagerId: userState.selectedManagerId,
        selectedTeamLeaderId: userState.selectedTeamLeaderId,
        managers: parentType === 'admin' ? [] : userState.managers, 
        teamLeaders: parentType !== 'tl' ? [] : userState.teamLeaders, 
    };

    if (parentType === 'admin') {
        update.selectedAdminId = id;
        update.selectedManagerId = null;
        update.selectedTeamLeaderId = null;
        if (id !== null && newRole === 'MANAGER') {
            update.selectedParentId = id; 
        } else if (id !== null && (newRole === 'TEAM_LEADER' || newRole === 'AGENT')) {
            fetchManagers(userId, id); 
        }
    } else if (parentType === 'manager') {
        update.selectedManagerId = id;
        update.selectedTeamLeaderId = null;
        if (id !== null && newRole === 'TEAM_LEADER') {
            update.selectedParentId = id; 
        } else if (id !== null && newRole === 'AGENT') {
            fetchTeamLeaders(userId, id); 
        }
    } else if (parentType === 'tl') {
        update.selectedTeamLeaderId = id;
        if (id !== null && newRole === 'AGENT') {
            update.selectedParentId = id; 
        }
    }

    setUserStates(prev => ({
      ...prev,
      [userId]: { ...userState, ...update as PendingUserState },
    }));

  }, [userStates, fetchManagers, fetchTeamLeaders]);


  // --- Approval Logic ---
  const handleApproval = async (userId: number) => {
    const state = userStates[userId];
    if (!state.selectedRole || state.selectedParentId === null) {
      setError('Please select a role and a valid parent before approving.');
      return;
    }

    const approvalRequest: UpdateRoleRequest = {
      newRole: state.selectedRole,
      parentId: state.selectedParentId,
    };
    
    try {
      await updateRoleAndActivate(userId, approvalRequest); 
      
      setMessage(`User ${userId} approved as ${state.selectedRole}`)
      setPendingUsers(prev => prev.filter(u => u.id !== userId));
      setUserStates(prev => {
        const { [userId]: removed, ...rest } = prev;
        return rest;
      });
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Approval failed. Check server logs.');
    }
  };


  // --- Rendering Functions ---

  const renderRoleSelect = (userId: number) => {
    const state = userStates[userId] || initialUserState;
    const assignableRoles = getAssignableRoles(safeCurrentUserRole);

    return (
      <select 
        value={state.selectedRole} 
        onChange={(e) => handleRoleChange(userId, e.target.value)}
        className="p-2 border border-gray-300 dark:border-gray-600 rounded w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <option value="">Select Role</option>
        {assignableRoles.map(role => (
          <option key={role} value={role}>{role.replace('_', ' ')}</option>
        ))}
      </select>
    );
  };
  
  const renderParentSelect = (userId: number): React.JSX.Element => {
    const state = userStates[userId] || initialUserState;
    const { 
        selectedRole, selectedParentId, selectedAdminId, selectedManagerId, selectedTeamLeaderId,
        admins, managers, teamLeaders, loading 
    } = state;

    if (!selectedRole) {
        return <span className="text-gray-500 dark:text-gray-400 text-sm">Select Role first</span>;
    }

    if (selectedParentId !== null && selectedAdminId === null && selectedManagerId === null && selectedTeamLeaderId === null) {
        return <span className="text-green-600 dark:text-green-400 font-medium text-sm">Parent ID: {selectedParentId} (Fixed)</span>;
    }
    
    if (selectedParentId !== null && (selectedAdminId !== null || selectedManagerId !== null || selectedTeamLeaderId !== null)) {
        return <span className="text-green-600 dark:text-green-400 font-medium text-sm">Final Parent ID: {selectedParentId}</span>;
    }

    const parentElements: React.JSX.Element[] = []; 

    // --- Admin Select ---
    const needsAdminSelectForSA = safeCurrentUserRole === 'SUPERADMIN' && (selectedRole === 'MANAGER' || selectedRole === 'TEAM_LEADER' || selectedRole === 'AGENT');
    if (needsAdminSelectForSA) {
      parentElements.push(
        <select
          key="admin-select"
          value={selectedAdminId === null ? '' : selectedAdminId.toString()} 
          onChange={(e) => handleParentSelectChange(userId, 'admin', e.target.value)}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded w-full mb-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          disabled={loading && admins.length === 0}
        >
          <option value="">{loading && admins.length === 0 ? 'Loading Admins...' : 'Select Admin'}</option>
          {admins.map(a => (
            <option key={a.id} value={a.id.toString()}>{a.username}</option>
          ))}
        </select>
      );
    }
    
    // --- Manager Select ---
    const needsManagerSelectForSA = safeCurrentUserRole === 'SUPERADMIN' && selectedAdminId !== null && (selectedRole === 'TEAM_LEADER' || selectedRole === 'AGENT');
    const needsManagerSelectForAdmin = safeCurrentUserRole === 'ADMIN' && (selectedRole === 'TEAM_LEADER' || selectedRole === 'AGENT');
    
    if (needsManagerSelectForSA || needsManagerSelectForAdmin) {
      const isDropdownEnabled = safeCurrentUserRole === 'ADMIN' || (safeCurrentUserRole === 'SUPERADMIN' && selectedAdminId !== null);
      
      parentElements.push(
        <select
          key="manager-select"
          value={selectedManagerId === null ? '' : selectedManagerId.toString()}
          onChange={(e) => handleParentSelectChange(userId, 'manager', e.target.value)}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded w-full mb-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          disabled={loading || !isDropdownEnabled}
        >
          <option value="">{loading && managers.length === 0 ? 'Loading Managers...' : 'Select Manager'}</option>
          {managers.map(m => (
            <option key={m.id} value={m.id.toString()}>{m.username}</option>
          ))}
        </select>
      );
    }

    // --- Team Leader Select ---
    const needsTLSelectForSA = safeCurrentUserRole === 'SUPERADMIN' && selectedRole === 'AGENT' && selectedManagerId !== null;
    const needsTLSelectForAdmin = safeCurrentUserRole === 'ADMIN' && selectedRole === 'AGENT' && selectedManagerId !== null;
    const needsTLSelectForManager = safeCurrentUserRole === 'MANAGER' && selectedRole === 'AGENT';
    
    if (needsTLSelectForSA || needsTLSelectForAdmin || needsTLSelectForManager) {
      const isDropdownEnabled = safeCurrentUserRole === 'MANAGER' || selectedManagerId !== null;
      
      parentElements.push(
        <select
          key="tl-select"
          value={selectedTeamLeaderId === null ? '' : selectedTeamLeaderId.toString()}
          onChange={(e) => handleParentSelectChange(userId, 'tl', e.target.value)}
          className="p-2 border border-gray-300 dark:border-gray-600 rounded w-full mb-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          disabled={loading || !isDropdownEnabled}
        >
          <option value="">{loading && teamLeaders.length === 0 ? 'Loading TLs...' : 'Select Team Leader'}</option>
          {teamLeaders.map(tl => (
            <option key={tl.id} value={tl.id.toString()}>{tl.username}</option>
          ))}
        </select>
      );
    }

    if (parentElements.length === 0 && selectedParentId === null && selectedRole !== '') {
        return <span className="text-gray-500 dark:text-gray-400 text-sm">Please select a role to determine parent.</span>;
    }

    return (
        <div className="flex flex-col space-y-1">
            {selectedRole && <span className="text-gray-600 dark:text-gray-400 text-sm">Select Parent:</span>}
            {parentElements.length > 0 ? parentElements : <span className="text-red-500 dark:text-red-400 text-sm">Selection required.</span>}
        </div>
    );
  };
  
  return (
    <AdminLayout>
      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
        <h2 className="text-3xl font-bold mb-6 text-gray-800 dark:text-white">Pending User Approvals</h2>
        
        {message && <p className="text-green-600 dark:text-green-400 mb-4 font-semibold">{message}</p>}
        {error && <p className="text-red-600 dark:text-red-400 mb-4 font-semibold">{error}</p>}

        <div className="overflow-x-auto bg-white dark:bg-gray-800 shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Username</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Email</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Select Role</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Assign Parent</th>
                <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {pendingUsers.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-gray-500 dark:text-gray-400">
                    No pending users for approval.
                  </td>
                </tr>
              )}
              {pendingUsers.map(user => {
                const state = userStates[user.id] || initialUserState;
                const isReadyToApprove = state.selectedRole !== '' && state.selectedParentId !== null; 

                return (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 transition-colors">
                    <td className="py-3 px-4 border-b border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">{user.username}</td> 
                    <td className="py-3 px-4 border-b border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300">{user.email}</td>
                    <td className="py-3 px-4 border-b border-gray-200 dark:border-gray-700">
                      <div className='max-w-[150px]'>{renderRoleSelect(user.id)}</div>
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 dark:border-gray-700">
                      <div className='min-w-[200px] max-w-xs'>{renderParentSelect(user.id)}</div>
                    </td>
                    <td className="py-3 px-4 border-b border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => handleApproval(user.id)}
                        disabled={!isReadyToApprove}
                        className={`px-4 py-2 rounded text-white font-semibold transition duration-150 shadow-sm ${
                          isReadyToApprove ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed dark:bg-gray-600'
                        }`}
                      >
                        Approve
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PendingApprovalsPage;